import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import {
  Award,
  CheckCircle,
  Mail,
  Phone,
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
} from "lucide-react";
import falcoLogo from "@/assets/falco.jpeg";

const LoginPage = () => {
  const navigate = useNavigate();
  const { user, setUser, checkAuth } = useAuth();
  
  // Check if user is already logged in
  useEffect(() => {
    if (user) {
      if (!user.profile_completed) {
        navigate("/complete-profile");
      } else if (!user.onboarding_completed) {
        navigate("/onboarding");
      } else {
        navigate("/dashboard");
      }
    }
  }, [user, navigate]);
  
  const [mode, setMode] = useState("login");
  const [authMethod, setAuthMethod] = useState("email");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [demoOtp, setDemoOtp] = useState("");

  const handleSendOtp = async () => {
    try {
      setLoading(true);
      const payload = authMethod === "email" ? { email } : { phone };
      const response = await axios.post(`${API}/auth/send-otp`, payload);
      setOtpSent(true);
      if (response.data.demo_otp) {
        setDemoOtp(response.data.demo_otp);
        toast.success(`OTP sent! Demo: ${response.data.demo_otp}`);
      } else {
        toast.success("OTP sent!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    
    try {
      setLoading(true);
      const payload = {
        name,
        email: authMethod === "email" ? email : undefined,
        phone: authMethod === "phone" ? phone : undefined,
        password: password || undefined,
      };
      
      const response = await axios.post(`${API}/auth/register`, payload);
      // Save token to localStorage
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      setUser(response.data.user);
      toast.success("Registration successful!");
      navigate("/complete-profile");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      const payload = {
        email: authMethod === "email" ? email : undefined,
        phone: authMethod === "phone" ? phone : undefined,
        password: !otpSent ? password : undefined,
        otp: otpSent ? otp : undefined,
      };
      
      const response = await axios.post(`${API}/auth/login`, payload);
      // Save token to localStorage
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
      setUser(response.data.user);
      toast.success("Login successful!");
      
      if (!response.data.user.profile_completed) {
        navigate("/complete-profile");
      } else if (!response.data.user.onboarding_completed) {
        navigate("/onboarding");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left - Form */}
      <div className="flex flex-col justify-center px-6 py-8 sm:px-8 lg:px-16 bg-white">
        <div className="max-w-md mx-auto w-full">
          {/* Logo - Bigger */}
         <div className="flex items-center gap-4 mb-8">
          <img
            src={falcoLogo}
            alt="AnswerCraft"
            className="h-24 sm:h-28 w-24 sm:w-28 object-cover object-center"
            style={{ borderRadius: "0" }}
            data-testid="login-logo"
          />
          <h2 className="text-3xl font-extrabold leading-none tracking-tight">
            <span style={{ color: "#2E3A59" }}>Answer</span>
            <span style={{ color: "#8B6914" }}>Craft</span>
            <sup style={{ color: "#8B6914", fontSize: "0.45em", verticalAlign: "super" }}>™</sup>
          </h2>
        </div>

          <h1 className="text-2xl sm:text-3xl font-bold text-[#2E3A59] mb-2">
            {mode === "register" ? "Create Account" : "Welcome Back"}
          </h1>
          <p className="text-slate-600 mb-6">
            {mode === "register" ? "Join thousands of MBBS students" : "RGUHS Practice Platform"}
          </p>

         

          {/* Auth Method Toggle */}
          <div className="flex gap-2 mb-4">
            <Button
              type="button"
              variant={authMethod === "email" ? "default" : "outline"}
              size="sm"
              onClick={() => setAuthMethod("email")}
              className={authMethod === "email" ? "bg-[#2E3A59]" : ""}
              data-testid="btn-email-auth"
            >
              <Mail className="w-4 h-4 mr-2" />
              Email
            </Button>
            <Button
              type="button"
              variant={authMethod === "phone" ? "default" : "outline"}
              size="sm"
              onClick={() => setAuthMethod("phone")}
              className={authMethod === "phone" ? "bg-[#2E3A59]" : ""}
              data-testid="btn-phone-auth"
            >
              <Phone className="w-4 h-4 mr-2" />
              Phone
            </Button>
          </div>

          <div className="space-y-4 mb-6">
            {mode === "register" && (
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Full Name</label>
                <Input
                  type="text"
                  placeholder="Dr. Your Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-12"
                  data-testid="input-name"
                />
              </div>
            )}

            {authMethod === "email" ? (
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Email</label>
                <Input
                  type="email"
                  placeholder="you@college.edu"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12"
                  data-testid="input-email"
                />
              </div>
            ) : (
              <div>
                <label className="text-sm text-slate-600 mb-1 block">Phone</label>
                <Input
                  type="tel"
                  placeholder="+91 98765 43210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="h-12"
                  data-testid="input-phone"
                />
              </div>
            )}

            {!otpSent ? (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm text-slate-600">Password</label>
                  {mode === "login" && (
                    <button type="button" onClick={handleSendOtp} className="text-xs text-[#2E3A59] hover:underline">
                      Use OTP instead
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 pr-10"
                    data-testid="input-password"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm text-slate-600">Enter OTP</label>
                  <button type="button" onClick={() => setOtpSent(false)} className="text-xs text-[#2E3A59] hover:underline">
                    Use password
                  </button>
                </div>
                <Input
                  type="text"
                  placeholder="6-digit OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={6}
                  className="h-12 text-center tracking-widest text-lg"
                  data-testid="input-otp"
                />
                {demoOtp && <p className="text-xs text-emerald-600 mt-1">Demo OTP: {demoOtp}</p>}
              </div>
            )}
          </div>

          <Button
            onClick={mode === "register" ? handleRegister : handleLogin}
            disabled={loading}
            className="w-full h-12 bg-[#2E3A59] hover:bg-[#1A2236] rounded-xl mb-4"
            data-testid="btn-submit"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
              <>
                {mode === "register" ? "Create Account" : "Sign In"}
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>

          <p className="text-sm text-slate-500 text-center mt-4">
            {mode === "register" ? (
              <>Already have an account? <button onClick={() => setMode("login")} className="text-[#2E3A59] font-medium hover:underline" data-testid="btn-to-login">Sign In</button></>
            ) : (
              <>Don't have an account? <button onClick={() => setMode("register")} className="text-[#2E3A59] font-medium hover:underline" data-testid="btn-to-register">Create Account</button></>
            )}
          </p>
        </div>
      </div>

      {/* Right - Hero */}
      <div className="hidden lg:flex flex-col justify-center px-16 bg-gradient-to-br from-[#2E3A59] to-[#1A2236] text-white">
        <div className="max-w-md">
          <h2 className="text-3xl font-bold mb-4">
            Practice Smarter, <span className="text-[#C9A227]">Score Higher</span>
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            AI-powered answer evaluation for RGUHS MBBS students.
          </p>

          <div className="space-y-4 mb-12">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Examiner-Like Feedback</p>
                <p className="text-sm text-slate-400">Detailed marks breakdown</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">RGUHS PYQs 2021-2025</p>
                <p className="text-sm text-slate-400">Real university questions</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Track Weak Areas</p>
                <p className="text-sm text-slate-400">Improve systematically</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6 mb-12">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#C9A227]">878</div>
              <div className="text-sm text-slate-300">Questions</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#C9A227]">3</div>
              <div className="text-sm text-slate-300">Subjects</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#C9A227]">AI</div>
              <div className="text-sm text-slate-300">Powered</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
